import { Inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Observable, Subject, filter, map, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Group } from '../models/group';
import { authConfig } from 'main';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalBroadcastService: MsalBroadcastService,
    private http: HttpClient
  ) {
    this.authService.initialize();
  }

  // Login
  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  // Logout
  logout() {
    this.authService.logoutRedirect({
      account: this.authService.instance.getActiveAccount(),
    });
  }

  // Login success obs
  loginSucess(): Observable<AccountInfo> {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter(
        (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
      ),
      takeUntil(this._destroying$),
      map((result: EventMessage) => {
        const account = result.payload as AccountInfo;
        this.authService.instance.setActiveAccount(account);
        return account;
      })
    )
  }

  // Progress obs
  inProgress(): Observable<AccountInfo | null> {
    return this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$),
        map(() => {
          return this.checkAndSetActiveAccount();
        })
      )
  }

  // Logout success obs
  logoutSucess(): Observable<void> {
    return this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this._destroying$),
        map(() => {
          this.checkAndSetActiveAccount();
        })
      )
  }

  // If user is login
  isLogin() : boolean {
    if(this.authService.instance.getActiveAccount()) {
      return true;
    } else {
      return false;
    }
  }

  // If is set login display
  setLoginDisplay(): boolean {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  // Check and set active account
  checkAndSetActiveAccount(): AccountInfo | null {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      activeAccount = accounts[0];
    }

    this.authService.instance.setActiveAccount(activeAccount);

    return activeAccount;
  }

  // Get login user groups
  getLoginUserRoles() : Observable<Group[]>  {
    return this.http.get<any>(authConfig.graphUrl + "/me/memberOf").pipe(
      map(data => (data.value as any[]).map(value => ({ id: value.id, name: value.displayName }) as Group))
    )
  }

  getAccessToken(): Observable<string> {
    return this.authService.acquireTokenSilent({scopes: [authConfig.scope!], account: this.authService.instance.getActiveAccount() as AccountInfo})
    .pipe(
      map(res => res.accessToken)
    );
  }

  // Destroy
  destroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
